import { Icon } from '../icon';
import { MaterialIcon } from '../icon/icons';
import { addStyles } from '../theme';
import { T, Translate } from '../../i18n';
import { m, type MithrilVNode } from '../../mithril';
import { CSS, Style } from './color.picker.style';

addStyles(Style);

export interface ColorPickerAttrs {
	color: string;
	palette: readonly string[];
	onPickColor: (color: string) => void;
}

// Every dot gets a subtle inset edge (the desired "border"). The selected ring adds the dot's own
// color with a white gap (NOT the accent), set inline per dot so each ring matches its swatch.
const DOT_INSET = 'inset 0 0 0 1px rgba(0, 0, 0, 0.14)';
function dotShadow(color: string, active: boolean): string {
	return active ? `${DOT_INSET}, 0 0 0 2px #fff, 0 0 0 4px ${color}` : DOT_INSET;
}

/** Presentational — pure props-in / callbacks-out, no observable state, so it ports cleanly to a React component later. */
export const ColorPicker = {
	view(attrs: ColorPickerAttrs): MithrilVNode {
		const { color, palette, onPickColor } = attrs;
		const current = color.toLowerCase();
		const dots = palette.map((swatch) => {
			const active = swatch.toLowerCase() === current;
			return m(
				'button',
				{
					type: 'button',
					className: CSS.dot,
					title: swatch,
					style: { background: swatch, 'box-shadow': dotShadow(swatch, active) },
					onclick: () => onPickColor(swatch),
				},
				active ? Icon.view(MaterialIcon.CHECK) : null
			);
		});
		dots.push(viewCustomDot(attrs, current));
		return m('div', { className: CSS.wrap }, dots);
	},
};

/**
 * Commit live on `input` so the swatch tracks the OS picker as it drags. Each commit is a MobX mutation,
 * but `RenderService` throttles the repaint to ~60fps and the caller's mutation is cheap — no re-render storm.
 */
function viewCustomDot(attrs: ColorPickerAttrs, current: string): MithrilVNode {
	const { palette, onPickColor } = attrs;
	const isPreset = palette.some((color) => color.toLowerCase() === current);
	const glyphs = isPreset
		? [Icon.view(MaterialIcon.ADD)]
		: [
				Icon.view(MaterialIcon.CHECK, { className: CSS.customIconCheck }),
				Icon.view(MaterialIcon.EDIT, { className: CSS.customIconEdit }),
			];
	return m(
		'label',
		{
			className: isPreset ? `${CSS.dot} ${CSS.customDot} ${CSS.customDotIdle}` : `${CSS.dot} ${CSS.customDot}`,
			title: Translate(T.COLOR_CUSTOM),
			// Always set both explicitly so deselecting (off-palette → preset) clears the prior fill/ring.
			style: isPreset
				? { background: 'transparent', 'box-shadow': 'none' }
				: { background: current, 'box-shadow': dotShadow(current, true) },
		},
		[
			// Input FIRST so it stays the same DOM node across idle→selected (glyphs change count): if it
			// moved/recreated mid-pick the open OS picker would be torn down. It's absolute, so still on top.
			m('input', {
				type: 'color',
				className: CSS.customInput,
				value: current,
				oninput: (event: Event) => onPickColor((event.target as HTMLInputElement).value),
			}),
			...glyphs,
		]
	);
}
